import { parseISO, startOfDay } from "date-fns"
import type { TFunction } from "i18next"
import i18n from "@/lib/i18n/i18n"
import { DriverDisplayInfo } from "@/reducers/accountChatsSelectors"
import {
  type GboxTimelineEvent as FeedEvent,
  type GboxTimelineFeed,
} from "@/lib/gboxTimeline"
import { Chat } from "@/reducers/chatsSlice"

export type TimelineTone = "danger" | "info" | "neutral" | "success" | "warning"
export type TimelineEventKind = "delivery" | "other" | "pickup"

export type TimelineSegment = {
  endCode: string
  label: string
  span: number
  startCode: string
  startColumn: number
  tone: TimelineTone
}

export type TimelineChainMilestone = {
  code: string
  kind: TimelineEventKind
  offset: number
  timeLabel: string
  tone: TimelineTone
}

export type TimelineChainEvent = {
  chainId: string
  code: string
  countryCode?: string
  customer?: string
  delaySeconds?: number
  delayWithTachoSeconds?: number
  endOffset: number
  errandId: string
  errandStatusLabel?: string
  estimatedArrivalTime?: string
  estimatedArrivalTimeWithTacho?: string
  eventId: string
  externalStatus?: string
  isCompleted: boolean
  isCurrentErrand: boolean
  isCurrentEvent: boolean
  isOnRoute: boolean
  kind: TimelineEventKind
  kindLabel: string
  offset: number
  realTime?: string
  referenceNumber?: string
  scheduledTime?: string
  sequence?: number
  startOffset: number
  subtitle?: string
  timeLabel: string
  timelinessStatus?: string
  title: string
  tone: TimelineTone
}

export type TimelineChain = {
  // Every event of a chain belongs to one transport, so they all report the
  // same customer — lifted here so the planner can filter vehicle rows by it
  // without walking the events. Undefined for feeds that don't know it.
  customer?: string
  endCode: string
  endOffset: number
  events: TimelineChainEvent[]
  key: string
  label: string
  lane: number
  milestones: TimelineChainMilestone[]
  startCode: string
  startOffset: number
  subtitle?: string
  tone: TimelineTone
}

export type TimelineRow = {
  alerts: string[]
  chains?: TimelineChain[]
  chatJid?: string
  dispatcherCode: string
  driverName: string
  laneCount?: number
  roomName?: string
  segments: TimelineSegment[]
}

export type TimelineSummary = {
  label: string
  value: string
}

export type TimelineData = {
  dateOffsets: number[]
  rows: TimelineRow[]
  subtitle: string
  summary: TimelineSummary[]
  title: string
}

type EventDraft = {
  code: string
  date: Date
  event: FeedEvent
  kind: TimelineEventKind
  offset: number
  tone: TimelineTone
}

type ChainDraft = {
  chainId: string
  customer?: string
  endCode: string
  endOffset: number
  events: EventDraft[]
  label: string
  startCode: string
  startOffset: number
  subtitle?: string
  tone: TimelineTone
}

type RowDraft = {
  alerts: string[]
  chains: TimelineChain[]
  chatJid?: string
  dispatcherCode: string
  driverName: string
  laneCount: number
  roomName?: string
  segments: TimelineSegment[]
  sortKey: [number, string]
  visibleEventCount: number
}

const DEFAULT_DATE_OFFSETS = [-1, 0, 1, 2, 3, 4, 5]
const DAY_IN_MS = 24 * 60 * 60 * 1000
const MIN_CHAIN_DURATION_DAYS = 0.14

export const buildTimelineData = (
  drivers: DriverDisplayInfo[],
  feeds: GboxTimelineFeed[],
  chats: Chat[],
  now: Date = new Date(),
  // Callers that memoize the result pass their hook's `t`, so a language
  // switch rebuilds the labels.
  t: TFunction = i18n.t,
): TimelineData | null => {
  const chatsByJid = new Map(chats.map((chat) => [chat.jid, chat]))
  const timelineStart = startOfDay(now)
  const rowDrafts = feeds
    .map((feed) => buildRowDraft(feed, drivers, timelineStart, chatsByJid, t))
    .filter((row): row is RowDraft => row !== null)

  if (rowDrafts.length === 0) {
    return null
  }

  rowDrafts.sort((left, right) => {
    if (left.sortKey[0] !== right.sortKey[0]) {
      return left.sortKey[0] - right.sortKey[0]
    }
    return left.sortKey[1].localeCompare(right.sortKey[1])
  })

  const dateOffsets = buildDateOffsets(rowDrafts)
  const alertCount = rowDrafts.reduce((sum, row) => sum + row.alerts.length, 0)
  const visibleEventCount = rowDrafts.reduce(
    (sum, row) => sum + row.visibleEventCount,
    0,
  )

  return {
    title: t("planner.liveTimeline.title"),
    subtitle: t("planner.liveTimeline.subtitle"),
    dateOffsets,
    summary: [
      {
        label: t("planner.liveTimeline.summary.drivers"),
        value: rowDrafts.length.toString(),
      },
      {
        label: t("planner.liveTimeline.summary.alerts"),
        value: alertCount.toString(),
      },
      {
        label: t("planner.liveTimeline.summary.visibleEvents"),
        value: visibleEventCount.toString(),
      },
    ],
    rows: rowDrafts.map((row) => ({
      alerts: row.alerts,
      chains: row.chains,
      chatJid: row.chatJid,
      dispatcherCode: row.dispatcherCode,
      driverName: row.driverName,
      laneCount: row.laneCount,
      roomName: row.roomName,
      segments: row.segments,
    })),
  }
}

const buildRowDraft = (
  feed: GboxTimelineFeed,
  drivers: DriverDisplayInfo[],
  timelineStart: Date,
  chatsByJid: Map<string, Chat>,
  t: TFunction,
): RowDraft | null => {
  const driver = findDriverForFeed(feed, drivers)
  const drafts = feed.events
    .map((event) => buildEventDraft(event, timelineStart))
    .filter((event): event is EventDraft => event !== null)
    .sort(compareEventDrafts)

  if (drafts.length === 0) {
    return null
  }

  const chainDrafts = buildChainDrafts(drafts, t)
  const { chains, laneCount } = assignChainLanes(chainDrafts)
  const alerts = buildAlerts(feed, driver)
  // Sort by when the vehicle becomes free (end of all its work), ascending —
  // soonest-available first. This is a single unified ordering: a vehicle that
  // is busy now but finishes soon ranks above one that is idle now but has
  // later-scheduled work.
  const freeAtMs = Math.max(...drafts.map((draft) => draft.date.getTime()))

  const chatJid = driver?.chatJid || feed.chatJid
  const roomName = chatJid ? chatsByJid.get(chatJid)?.name : undefined

  return {
    alerts,
    chains,
    chatJid,
    dispatcherCode:
      driver?.plates?.trim() ||
      feed.plateNumber ||
      feed.deviceId ||
      driver?.routeJid?.split("@")[0] ||
      "GBOX",
    driverName:
      driver?.name ||
      feed.driverJid ||
      feed.chatJid ||
      feed.entityJid ||
      feed.deviceId,
    laneCount,
    roomName,
    segments: [],
    sortKey: [
      freeAtMs,
      (driver?.name || feed.driverJid || feed.deviceId).toLowerCase(),
    ],
    visibleEventCount: drafts.length,
  }
}

const findDriverForFeed = (
  feed: GboxTimelineFeed,
  drivers: DriverDisplayInfo[],
): DriverDisplayInfo | undefined => {
  return drivers.find((driver) =>
    Boolean(
      (feed.chatJid &&
        (driver.chatJid === feed.chatJid ||
          driver.routeJid === feed.chatJid)) ||
        // Timeline feeds may identify the driver by driverA/profile JID.
        (feed.driverJid &&
          (driver.profile?.jid || driver.jid) === feed.driverJid) ||
        (feed.entityJid &&
          (driver.chatJid === feed.entityJid ||
            driver.routeJid === feed.entityJid ||
            (driver.profile?.jid || driver.jid) === feed.entityJid)),
    ),
  )
}

const buildEventDraft = (
  event: FeedEvent,
  timelineStart: Date,
): EventDraft | null => {
  const date = getTimelineDate(event)
  if (!date) {
    return null
  }

  return {
    code: normalizeCountryCode(event.countryCode),
    date,
    event,
    kind: classifyEventKind(event),
    offset: toDayOffset(date, timelineStart),
    tone: getEventTone(event),
  }
}

const buildChainDrafts = (drafts: EventDraft[], t: TFunction): ChainDraft[] => {
  const grouped = drafts.reduce((acc, draft) => {
    const bucket = acc.get(draft.event.chainId) ?? []
    bucket.push(draft)
    acc.set(draft.event.chainId, bucket)
    return acc
  }, new Map<string, EventDraft[]>())

  return Array.from(grouped.entries())
    .map(([chainId, chainEvents]) => {
      const ordered = [...chainEvents].sort(compareEventDrafts)
      const first = ordered[0]
      const last = ordered[ordered.length - 1]
      const startOffset = first.offset
      const endOffset = Math.max(
        last.offset,
        first.offset + MIN_CHAIN_DURATION_DAYS,
      )
      const deliveryCount = ordered.filter(
        (event) => event.kind === "delivery",
      ).length
      const hasPickup = ordered.some((event) => event.kind === "pickup")
      const label = buildChainLabel(first.event, last.event, ordered.length)
      const subtitle = buildChainSubtitle(
        first.event,
        last.event,
        deliveryCount,
        hasPickup,
        t,
      )

      return {
        chainId,
        customer: ordered.find((event) => event.event.customer)?.event.customer,
        endCode: last.code,
        endOffset,
        events: ordered,
        label,
        startCode: first.code,
        startOffset,
        subtitle,
        tone: getChainTone(ordered),
      }
    })
    .sort((left, right) => left.startOffset - right.startOffset)
}

const assignChainLanes = (chainDrafts: ChainDraft[]) => {
  const lastOffsetByLane: number[] = []

  const chains = chainDrafts.map((chain) => {
    let lane = lastOffsetByLane.findIndex(
      (lastOffset) => chain.startOffset - lastOffset >= 0.12,
    )
    if (lane === -1) {
      lane = lastOffsetByLane.length
      lastOffsetByLane.push(Number.NEGATIVE_INFINITY)
    }
    lastOffsetByLane[lane] = chain.endOffset

    return {
      customer: chain.customer,
      endCode: chain.endCode,
      endOffset: chain.endOffset,
      key: chain.chainId,
      label: chain.label,
      lane,
      milestones: chain.events.map((event) => ({
        code: event.code,
        kind: event.kind,
        offset: event.offset,
        timeLabel: formatTimeLabel(event.date),
        tone: event.tone,
      })),
      events: chain.events.map((event) => ({
        chainId: event.event.chainId,
        code: event.code,
        countryCode: event.event.countryCode,
        customer: event.event.customer,
        delaySeconds: event.event.delaySeconds,
        delayWithTachoSeconds: event.event.delayWithTachoSeconds,
        endOffset: chain.endOffset,
        errandId: event.event.errandId,
        errandStatusLabel: event.event.errandStatusLabel,
        estimatedArrivalTime: event.event.estimatedArrivalTime,
        estimatedArrivalTimeWithTacho:
          event.event.estimatedArrivalTimeWithTacho,
        eventId: event.event.eventId,
        externalStatus: event.event.externalStatus,
        isCompleted: event.event.isCompleted,
        isCurrentErrand: event.event.isCurrentErrand,
        isCurrentEvent: event.event.isCurrentEvent,
        isOnRoute: event.event.isOnRoute,
        kind: event.kind,
        kindLabel: event.event.kindLabel,
        offset: event.offset,
        realTime: event.event.realTime,
        referenceNumber: event.event.referenceNumber,
        scheduledTime: event.event.scheduledTime,
        sequence: event.event.sequence,
        startOffset: chain.startOffset,
        subtitle: event.event.subtitle,
        timeLabel: formatTimeLabel(event.date),
        timelinessStatus: event.event.timelinessStatus,
        title: event.event.title,
        tone: event.tone,
      })),
      startCode: chain.startCode,
      startOffset: chain.startOffset,
      subtitle: chain.subtitle,
      tone: chain.tone,
    }
  })

  return { chains, laneCount: Math.max(lastOffsetByLane.length, 1) }
}

const buildChainLabel = (
  first: FeedEvent,
  last: FeedEvent,
  eventCount: number,
): string => {
  if (eventCount <= 1) {
    return first.title
  }
  if (first.title === last.title) {
    return first.title
  }
  return `${first.title} -> ${last.title}`
}

const buildChainSubtitle = (
  first: FeedEvent,
  last: FeedEvent,
  deliveryCount: number,
  hasPickup: boolean,
  t: TFunction,
): string | undefined => {
  const firstRef = first.referenceNumber
  const lastRef = last.referenceNumber

  if (!hasPickup) {
    return firstRef
  }
  if (deliveryCount > 1 && firstRef) {
    return t("planner.liveTimeline.chainDeliveries", {
      reference: firstRef,
      count: deliveryCount,
    })
  }
  if (firstRef && lastRef && firstRef !== lastRef) {
    return `${firstRef} -> ${lastRef}`
  }
  return firstRef || lastRef
}

const buildAlerts = (
  feed: GboxTimelineFeed,
  driver?: DriverDisplayInfo,
): string[] => {
  const alerts: string[] = []
  const lateCount = feed.events.filter(isLateEvent).length
  const currentEvent = feed.events.find((event) => event.isCurrentEvent)
  const openChainCount = new Set(
    feed.events
      .filter((event) => event.isCurrentErrand || !event.isCompleted)
      .map((event) => event.chainId),
  ).size

  if (driver?.isOffRoute) {
    alerts.push("Driver is off route")
  }
  if (lateCount > 0) {
    alerts.push(
      lateCount === 1
        ? "1 visible event is late"
        : `${lateCount.toString()} visible events are late`,
    )
  }
  if (currentEvent) {
    alerts.push(`Current stop: ${currentEvent.title.toLowerCase()}`)
  } else if (openChainCount > 0) {
    alerts.push(
      openChainCount === 1
        ? "1 active chain on vehicle"
        : `${openChainCount.toString()} active chains on vehicle`,
    )
  }

  if (alerts.length === 0) {
    const chainCount = new Set(feed.events.map((event) => event.chainId)).size
    alerts.push(
      chainCount === 1
        ? "1 visible chain on vehicle"
        : `${chainCount.toString()} visible chains on vehicle`,
    )
  }

  return alerts.slice(0, 3)
}

const buildDateOffsets = (rows: RowDraft[]): number[] => {
  const offsets = rows.flatMap((row) =>
    row.chains.flatMap((chain) => [chain.startOffset, chain.endOffset]),
  )
  if (offsets.length === 0) {
    return DEFAULT_DATE_OFFSETS
  }

  const minOffset = Math.max(Math.floor(Math.min(...offsets, -1)), -7)
  const maxOffset = Math.min(Math.ceil(Math.max(...offsets, 5)), 14)

  return Array.from(
    { length: maxOffset - minOffset + 1 },
    (_, index) => minOffset + index,
  )
}

const getTimelineDate = (event: FeedEvent): Date | null => {
  const rawValue =
    event.scheduledTime ||
    event.estimatedArrivalTimeWithTacho ||
    event.estimatedArrivalTime

  if (!rawValue) {
    return null
  }

  const parsed = parseISO(rawValue)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }

  return parsed
}

const compareEventDrafts = (left: EventDraft, right: EventDraft): number => {
  const leftTime = left.date.getTime()
  const rightTime = right.date.getTime()
  if (leftTime !== rightTime) {
    return leftTime - rightTime
  }
  if (left.event.isCurrentEvent && !right.event.isCurrentEvent) {
    return -1
  }
  if (!left.event.isCurrentEvent && right.event.isCurrentEvent) {
    return 1
  }
  return left.event.eventId.localeCompare(right.event.eventId)
}

const classifyEventKind = (event: FeedEvent): TimelineEventKind => {
  if (event.kind === "pickup" || event.kind === "delivery") {
    return event.kind
  }

  const typeName = event.kindLabel.toLowerCase()
  if (typeName.includes("załad") || typeName.includes("zalad")) {
    return "pickup"
  }
  if (typeName.includes("rozład") || typeName.includes("rozlad")) {
    return "delivery"
  }
  return "other"
}

const isLateEvent = (event: FeedEvent): boolean => {
  if (event.timelinessStatus === "late") {
    return true
  }
  const delay = event.delayWithTachoSeconds ?? event.delaySeconds
  return typeof delay === "number" && delay > 0
}

const getEventTone = (event: FeedEvent): TimelineTone => {
  if (isLateEvent(event)) {
    return "danger"
  }
  if (event.isCurrentEvent) {
    return "success"
  }
  if (event.isCurrentErrand) {
    return "warning"
  }
  if (event.kind === "pickup") {
    return "warning"
  }
  return "neutral"
}

const getChainTone = (events: EventDraft[]): TimelineTone => {
  if (events.some((event) => event.tone === "danger")) {
    return "danger"
  }
  if (
    events.some(
      (event) => event.event.isCurrentEvent || event.event.isCurrentErrand,
    )
  ) {
    return "success"
  }
  if (events.every((event) => event.event.isCompleted)) {
    return "neutral"
  }
  return "info"
}

const formatTimeLabel = (date: Date) =>
  `${date.getHours().toString().padStart(2, "0")}:${date
    .getMinutes()
    .toString()
    .padStart(2, "0")}`

const toDayOffset = (date: Date, timelineStart: Date): number =>
  (date.getTime() - timelineStart.getTime()) / DAY_IN_MS

const normalizeCountryCode = (countryCode?: string): string => {
  if (countryCode && /^[A-Za-z]{2}$/.test(countryCode)) {
    return countryCode.toUpperCase()
  }
  return "UN"
}
